// SummaryGPT Content - Brand First, Tool Agnostic
// Clean, consistent messaging across all pages

export const siteConfig = {
  name: "SummaryGPT",
  tagline: "AI that delivers.",
  description: "We build AI tools that produce real outputs — plans you can execute, data you can use, decisions you can make.",
  email: "hello@summarygpt.com",
  url: "https://summarygpt.com",
};

// HERO - 100% Brand focused
export const heroContent = {
  headline: "AI that delivers.",
  subheadline: "We build tools that produce real outputs — plans you can execute, data you can use, decisions you can make. No fluff. No prompt engineering. Just results.",
  cta: {
    text: "See what we build",
    url: "/tools"
  }
};

// BRAND APPROACH - Option A: How We Think (Tool Agnostic)
export const brandApproach = {
  headline: "Our approach.",
  principles: [
    {
      id: "01",
      title: "We design backwards",
      description: "Every tool starts with the output you need — then we work back to the simplest input."
    },
    {
      id: "02",
      title: "We close the gap",
      description: "Most AI creates more work. We eliminate the space between 'generated' and 'done.'"
    },
    {
      id: "03",
      title: "We solve one problem well",
      description: "No Swiss Army knives. Each tool does one thing — and finishes it."
    }
  ]
};

// TOOLS - Each with clear identity
export const tools = [
  {
    id: "epic",
    name: "Epic",
    tagline: "From idea to project plan in minutes.",
    description: "Describe your project. Get a complete work breakdown structure with phases, tasks, timelines, and deliverables. Ready to execute.",
    status: "live",
    appUrl: "https://epic.summarygpt.com",
    internalUrl: "/epic",
    highlight: "Used by product teams to plan launches, migrations, and MVPs.",
    whatItIs: "Epic is an AI project planner that transforms your project description into a structured, actionable plan. No templates. No guesswork. Just a clear path from start to finish.",
    whoItsFor: "Product managers, founders, team leads, and anyone who needs to turn an idea into an executable plan.",
    whatYouGet: [
      "Complete work breakdown structure (WBS)",
      "Phase-by-phase timeline with realistic estimates",
      "Task dependencies and milestones",
      "Export to CSV, JSON, or Markdown",
      "Shareable project briefs"
    ],
    exampleOutput: {
      title: "E-commerce MVP Launch",
      phases: [
        { name: "Discovery & Planning", duration: "2 weeks", tasks: 8 },
        { name: "Core Development", duration: "6 weeks", tasks: 24 },
        { name: "Testing & QA", duration: "2 weeks", tasks: 12 },
        { name: "Launch Preparation", duration: "1 week", tasks: 6 }
      ]
    },
    faqs: [
      {
        question: "What types of projects work best with Epic?",
        answer: "Epic works for any project — software builds, marketing campaigns, product launches, event planning, research projects. If it has phases and tasks, Epic can structure it."
      },
      {
        question: "How accurate are the time estimates?",
        answer: "Epic uses industry benchmarks and complexity analysis. Estimates are starting points — you can adjust based on your team's velocity."
      },
      {
        question: "Can I edit the plan after generation?",
        answer: "Yes. Export to your preferred format and customize. Epic gives you the structure; you own the execution."
      }
    ]
  },
  {
    id: "fork",
    name: "Fork",
    tagline: "Explore every angle. Decide faster.",
    description: "Branch your thinking into parallel paths. Compare options side-by-side. Converge on the best answer with confidence.",
    status: "early-access",
    appUrl: null,
    internalUrl: "/fork",
    highlight: "For decisions that deserve more than gut instinct.",
    whatItIs: "Fork is a visual thinking tool that lets you branch AI conversations into multiple paths. Explore different strategies, compare outcomes, and synthesize the best insights into one clear direction.",
    whoItsFor: "Strategists, executives, writers, and anyone facing decisions with multiple viable paths.",
    whatYouGet: [
      "Visual conversation branching",
      "Side-by-side path comparison",
      "Insight synthesis across branches",
      "Decision documentation",
      "Shareable decision trees"
    ],
    exampleOutput: {
      title: "Market Entry Strategy",
      branches: [
        { name: "Direct sales approach", insights: 4 },
        { name: "Partner channel strategy", insights: 5 },
        { name: "Hybrid model", insights: 3 }
      ]
    },
    faqs: [
      {
        question: "How is Fork different from ChatGPT?",
        answer: "ChatGPT is linear — one path forward. Fork lets you explore multiple directions simultaneously and compare them before committing."
      },
      {
        question: "When will Fork launch?",
        answer: "Fork is in private beta. Join the early access list to be first in line."
      }
    ]
  },
  {
    id: "snapstax",
    name: "SnapStax",
    tagline: "Receipts in. Clean data out.",
    description: "Photograph your receipts. Get structured expense data — vendor, amount, category, date — ready for your spreadsheet or accounting software.",
    status: "live",
    appUrl: "https://snapstax.summarygpt.com",
    internalUrl: "/snapstax",
    highlight: "Trusted by freelancers and small teams for expense tracking.",
    whatItIs: "SnapStax extracts structured data from receipt photos. No manual entry. No typos. Just clean, categorized expense records ready for export.",
    whoItsFor: "Freelancers, contractors, small business owners, and anyone who hates manual expense entry.",
    whatYouGet: [
      "Instant OCR extraction from photos",
      "Automatic expense categorization",
      "Multi-currency support",
      "Export to CSV, Excel, JSON",
      "Batch processing for multiple receipts"
    ],
    exampleOutput: {
      title: "March Expenses",
      items: [
        { vendor: "Office Supplies Co.", amount: "$234.50", category: "Office" },
        { vendor: "Cloud Services Inc.", amount: "$89.00", category: "Software" },
        { vendor: "Client Lunch", amount: "$67.80", category: "Meals" }
      ]
    },
    faqs: [
      {
        question: "What receipt formats work?",
        answer: "Photos, PDFs, scanned images. Thermal receipts, printed invoices, digital receipts — SnapStax handles them all."
      },
      {
        question: "Is my data secure?",
        answer: "Yes. We process your receipts securely and don't store images after extraction. Your financial data stays yours."
      },
      {
        question: "Does it work with my accounting software?",
        answer: "SnapStax exports to CSV and Excel — compatible with QuickBooks, Xero, FreshBooks, and most accounting tools."
      }
    ]
  },
  {
    id: "marginmate",
    name: "MarginMate",
    tagline: "Don't let a price change eat your margin.",
    description: "MarginMate watches your LCBO wholesale prices while you order, and shows exactly what to charge — right on the page. No spreadsheet, no guessing, no math at checkout.",
    status: "live",
    appUrl: "https://chrome.google.com/webstore", // PLACEHOLDER — swap for the real Chrome Web Store listing URL when available
    ctaLabel: "Add to Chrome — 2 minute setup",
    internalUrl: "/marginmate",
    highlight: "For stores ordering through wholesale.lcbo.com.",
    seoTitle: "MarginMate — Retail Pricing Assistant for LCBO Wholesale",
    seoDescription: "See your suggested retail price and profit right on wholesale.lcbo.com as you order — no spreadsheet, no re-calculating by hand.",
    whatItIs: "MarginMate is a free Chrome extension that shows your suggested retail price and profit the moment you land on a product at wholesale.lcbo.com — accounting for Ontario's 13% HST and the $0.10 bottle deposit automatically, so you never have to run the math yourself.",
    whoItsFor: "Independent convenience and variety store owners in Ontario who order alcohol through LCBO's wholesale site and need a fast, accurate answer for what to charge.",
    whatYouGet: [
      "See your price the second you land on a product — shows what to charge and what you'll make, based on the target margin you set once",
      "Catch a cost increase before it costs you — flagged the moment LCBO raises what you pay",
      "Change a price in one drag — one slider updates your price and profit together, no calculator needed",
      "Check out, and it's already logged — every order saved automatically, nothing extra to write down",
      "Save a custom price for any specific product and MarginMate remembers it",
      "One-time store setup — postal code and target margin, done in about 2 minutes",
      "Runs entirely in your browser — your numbers stay yours"
    ],
    exampleOutput: {
      title: "wholesale.lcbo.com — Live Preview",
      slides: [
        {
          kind: "price", layout: "grid", feature: "1. Know the price before you order",
          region: "Ontario", product: "Avit Lemon", size: "473 mL can",
          wholesalePrice: "$70.56", suggestedPrice: "$3.37", baseCost: "$2.51", profit: "$0.86", units: "{24 units}"
        },
        {
          kind: "alert", feature: "2. Catch a wholesale cost increase",
          region: "Ontario", product: "8pk Variety Pack", size: "8 x 355 mL can",
          wholesalePrice: "$61.32", previousPrice: "$58.10", increase: "$3.22"
        },
        {
          kind: "price", layout: "detail", feature: "3. Save a custom price per product",
          product: "Draught Stout", sku: "270447", size: "500 mL can",
          wholesalePrice: "$81.12", suggestedPrice: "$4.15", baseCost: "$2.90", profit: "$1.25", units: "{24 units}", savedPrice: true
        },
        {
          kind: "history", feature: "4. Every order logged automatically",
          orders: [
            { product: "Avit Lemon", sku: "427981", qty: "1 case", cost: "$70.56", date: "Jul 28, 2026" },
            { product: "Draught Stout", sku: "270447", qty: "2 cases", cost: "$162.24", date: "Jul 24, 2026" }
          ]
        }
      ]
    },
    roadmap: [
      { tag: "Coming Soon", icon: "📍", title: "See how you compare locally", body: "Once enough nearby stores are using MarginMate, you'll see whether your price sits above, within, or below the local range — never an exact number, never a competitor's name. Awareness, not a bidding war." },
      { tag: "Coming Soon", icon: "🎚️", title: "Category-level pricing targets", body: "Save a different target margin for whole categories — craft versus mainstream, fast movers versus slow ones — on top of the per-product pricing you can already save today." },
      { tag: "Coming Soon", icon: "🧰", title: "More tools, same install", body: "MarginMate is built for independent store owners ordering through LCBO. As it proves useful, we'll add more tools for the same job — always opt-in, always built for the business, never the other way around." }
    ],
    disclaimer: "MarginMate is an independent tool and is not affiliated with, endorsed by, or sponsored by the LCBO.",
    privacyUrl: "/marginmate/privacy",
    faqs: [
      { question: "Does MarginMate work anywhere besides LCBO's wholesale site?", answer: "No. MarginMate's content script only activates on wholesale.lcbo.com. It requests no other host permissions and doesn't run on any other site." },
      { question: "Is my data sent anywhere?", answer: "No. Your store profile, saved prices, price history, and order log are all stored locally in your browser via chrome.storage.local. MarginMate makes no network requests today. See our full privacy policy for details, including what may change in future versions." },
      { question: "Is MarginMate affiliated with or endorsed by the LCBO?", answer: "No. MarginMate is an independent tool built by SummaryGPT for Ontario retailers. It is not affiliated with, endorsed by, or sponsored by the LCBO." },
      { question: "Is MarginMate free?", answer: "Yes. MarginMate is free to use today, with no paid tier." }
    ]
  }
];

// SUBSCRIBE
export const subscribeContent = {
  headline: "New tools are coming.",
  description: "Get early access before public launch.",
  placeholder: "Your email",
  buttonText: "Get early access"
};

// Legacy exports for compatibility
export const whyPoints = brandApproach.principles;
export const brandStory = brandApproach;
